import axios from "axios";
import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";
import { useGlobalMessagesStore } from "./globalMessagesStore";
import { AuditDocument, AuditIncident, AuditReviewDocument, AuditStatus, PagedList } from "@/audit.types";
import { handleError } from "./storeHelper";
import { BTableOptions, serializeSortBy } from "@/components/BTable/BTable.types";
import { BFieldSelection, BFilterableField, BFilterOptions, dateTimeOperations, listOperations, textOperations } from "@/components/BFilterEditor/BFilterEditor.types";
import { serializeFilters } from "@/components/BTable/HelperFunctions";

export const useAuditIncidentStore = defineStore("auditIncidentStore", () => {
    const loading = ref<boolean>(false);
    const items = ref<AuditIncident[]>();
    const globalMessages = useGlobalMessagesStore();
    const apiUrl = 'api/v1/audit/incident';
    const auditId = ref<number>();
    const filterOptions = ref<BFilterOptions>({
        filters: [],
    });
    const tableOptions = ref<BTableOptions>({
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [],
        filterBy: [],
    } as BTableOptions);



    const statuses: BFieldSelection[] = [
        { text: "Narrative Requested", value: "NR" },
        { text: "Narrative Uploaded", value: "IN" },
        { text: "Narrative Rejected", value: "NRJ" },
        { text: "Passed", value: "IP" },
        { text: "Failed", value: "IF" },
    ];



    const filterableFields = reactive<BFilterableField[]>([
        {
            property: "incidentNumber",
            label: "Incident Number",
            operations: textOperations,
            editorComponent: "text-editor",
        },
        {
            property: "incidentDate",
            label: "Incident/Arrest Date",
            operations: dateTimeOperations,
            editorComponent: "date-editor"
        },
        {
            property: "status",
            label: "Status",
            operations: listOperations,
            editorComponent: "list-editor",
            editorOptions: statuses
        },
    ]);


    const list = async (id: number) => {

        auditId.value = id;
        try {
            loading.value = true;

            let filterParam = serializeFilters(filterOptions.value.filters, filterableFields);
            let sortParam = serializeSortBy(tableOptions.value.sortBy);

            const response = await axios.get<PagedList<AuditIncident>>(`${apiUrl}/${id}`, {
                params: {
                    limit: tableOptions.value.itemsPerPage,
                    offset: tableOptions.value.itemsPerPage * (tableOptions.value.page - 1),
                    sort: sortParam,
                    filter: filterParam,
                }
            });
            items.value = response.data.data;
            tableOptions.value.totalItemCount = response.data.total;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const getById = async (id: number): Promise<AuditIncident> => {

        try {
            loading.value = true;
            const response = await axios.get<AuditIncident>(`${apiUrl}/${id}`);
            return response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const uploadNarrative = async (id: number, files: any): Promise<void> => {
        try {
            loading.value = true;
            let formData = new FormData();
            formData.append('file', files.value.files[0]);
            await axios.post<AuditDocument>(`${apiUrl}/actions/${id}/upload`, formData);
            globalMessages.push({ message: "Narrative uploaded successfully.", type: "success" })
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }



    const checkIncidentsVersion = async (id: number): Promise<void> => {

        try {
            loading.value = true;
            await axios.post<AuditDocument>(`${apiUrl}/actions/${id}/check-incidents-version`);
            globalMessages.push({ message: "Reloaded successfully.", type: "success" })
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const reviewNarrative = async (review: AuditReviewDocument): Promise<void> => {

        try {
            loading.value = true;
            await axios.post<AuditDocument>(`${apiUrl}/actions/${review.id}/reject-narrative`, review);
            globalMessages.push({ message: "Narrative reviewed successfully.", type: "success" })
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const deleteItem = async (id: number): Promise<void> => {
        try {
            loading.value = true;
            await axios.delete(`${apiUrl}/${id}`);
            globalMessages.push({ message: "Incident deleted successfully.", type: "success" })
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const reviewIncident = async (review: AuditReviewDocument): Promise<void> => {

        try {
            loading.value = true;
            await axios.post<AuditDocument>(`${apiUrl}/actions/${review.id}/review-incident`, review);
            globalMessages.push({ message: "Incident reviewed successfully.", type: "success" })
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }


    return {
        tableOptions,
        filterOptions,
        filterableFields,
        loading,
        items,
        statuses,
        deleteItem,
        checkIncidentsVersion,
        uploadNarrative,
        reviewNarrative,
        reviewIncident,
        getById,
        list
    };
})